import { convertToNumber16, ModelChildChangedMessage, ModelChildChangedType, ModelParentChangedMessage, ModelRoomChangedMessage, RemoveFacetFromModelMessage, RenderVisibilityMessage, UpdateFacetOnModelMessage, Transform, LocalTransformFacet, VisibilityFacet, AttachToModelFacet, INVALID_UID, } from "flavr-common";
import { RoomTransformationUpdateMessage, UpdateLocalTransformationMessage } from "@messages/scenegraph/ScenegraphMessages";
import { MODEL_POOL_ACCESS_FOR_MODEL, modelPool } from "./ModelPool";
import { applyDefaults, DefaultTransformProps, emitOrDefer, LOCAL_MESSAGING, NO_MESSAGING, shouldBroadcast } from "./types";
import { ROOT_ID } from "./modelPath/ModelPath";
import { determineModelNameForPrinting } from "./modelPoolUtils";
export class Model {
    constructor(uid, name) {
        // Initialize general attributes
        this.uid = uid;
        this.name = name.replace("[", "").replace("]", "");
        // Initialize appearance transforms
        this.visible = true;
        this.transform = new Transform();
        this.worldTransform = new Transform();
        this.worldTransformDirty = true;
        // Facets
        this.facets = new Map();
        // Initialize scene attributes
        this.children = [];
    }
    /// General getters and setters ///
    getUID() { return this.uid; }
    getName() { return this.name; }
    isRoom() { return false; }
    isRoot() { return false; }
    setName(name, props) {
        this.name = name;
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelName(this, this.name, meta));
    }
    /**
     * Returns true if the other model shares the same uid as this model
     */
    equals(other) { return this.uid === other.getUID(); }
    /// Appearance getters and setters ///
    isVisible() { return this.visible; }
    setVisible(visible, props) {
        var _a, _b;
        const valueChanged = this.visible !== visible;
        const handleDescendants = (_a = props === null || props === void 0 ? void 0 : props.handleDescendants) !== null && _a !== void 0 ? _a : false;
        if (valueChanged) {
            this.visible = visible;
            emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelVisibility(this, this.visible, meta));
            const updateFacet = (_b = props === null || props === void 0 ? void 0 : props.updateFacet) !== null && _b !== void 0 ? _b : true;
            if (updateFacet)
                this.setFacet(new VisibilityFacet(visible), props);
            if (shouldBroadcast(props))
                this.queueMessage(new RenderVisibilityMessage(this.uid, this.visible), props);
        }
        // Handle descendants recursively
        if (handleDescendants)
            this.children.forEach((child) => {
                child.setVisible(visible, Object.assign(Object.assign({}, props), LOCAL_MESSAGING));
            });
    }
    invalidateWorldTransform(invalidateOnlyChildWorldTransforms = false) {
        if (!invalidateOnlyChildWorldTransforms)
            this.worldTransformDirty = true;
        this.children.forEach(child => { child.invalidateWorldTransform(); });
    }
    getTransform(mat) { return this.transform.getMatrix(mat); }
    getTransformComponents(pos, ori, sca) { return this.transform.getComponents(pos, ori, sca); }
    /**
     * Returns the world matrix of this model. The world matrix is computed from the local matrix and the parent transform
     */
    getWorldTransform(mat) {
        if (this.worldTransformDirty)
            this.recomputeWorldTransform();
        return this.worldTransform.getMatrix(mat);
    }
    /**
     * Returns the world transform components of this model. These components are computed from the local transform and
     * the parent transform components. Position, orientation and scale can be provided to reduce allocations.
     */
    getWorldTransformComponents(pos, ori, sca) {
        if (this.worldTransformDirty)
            this.recomputeWorldTransform();
        const position = this.worldTransform.getPosition(pos);
        const orientation = this.worldTransform.getOrientation(ori);
        const scale = this.worldTransform.getScale(sca);
        return { position, orientation, scale };
    }
    getPosition(pos) { return this.transform.getPosition(pos); }
    getOrientation(ori) { return this.transform.getOrientation(ori); }
    getScale(sca) { return this.transform.getScale(sca); }
    getWorldPosition(pos) {
        if (this.worldTransformDirty)
            this.recomputeWorldTransform();
        return this.worldTransform.getPosition(pos);
    }
    getWorldOrientation(ori) {
        if (this.worldTransformDirty)
            this.recomputeWorldTransform();
        return this.worldTransform.getOrientation(ori);
    }
    getWorldScale(sca) {
        if (this.worldTransformDirty)
            this.recomputeWorldTransform();
        return this.worldTransform.getScale(sca);
    }
    /**
     * Refreshes the local transform of this model. If `updateFacet` is set to true, this operation will also update
     * the LocalTransformFacet to the transform components of the transform matrix. Can be used to ensure that the
     * local transforms of all descendants are up to date
     *
     * @see {@link BroadcastProps}
     */
    refreshTransform(props) {
        this.invalidateWorldTransform();
        this.broadcastLocalTransformUpdate(props);
    }
    /**
     * Updates the local transform of this model. If `updateFacet` is set to true, this operation will also update
     * the LocalTransformFacet to the transform components of the transform matrix
     *
     * @see {@link BroadcastProps}
     */
    setTransform(transform, props) {
        this.transform.matrix = transform;
        this.invalidateWorldTransform();
        this.broadcastLocalTransformUpdate(props);
    }
    /**
     * Updates the local transform of this model. If `updateFacet` is set to true, this operation will also update
     * the LocalTransformFacet to the provided values.
     *
     * @see {@link BroadcastProps}
     */
    setTransformComponents(position, orientation, scale, props) {
        this.transform.setComponents(position, orientation, scale);
        this.invalidateWorldTransform();
        this.broadcastLocalTransformUpdate(props);
    }
    setPosition(position, props) {
        this.transform.position = position;
        this.invalidateWorldTransform();
        this.broadcastLocalTransformUpdate(props);
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelPosition(this, this.transform.position, meta));
    }
    setOrientation(orientation, props) {
        this.transform.orientation = orientation;
        this.invalidateWorldTransform();
        this.broadcastLocalTransformUpdate(props);
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelOrientation(this, this.transform.orientation, meta));
    }
    setScale(scale, props) {
        this.transform.scale = scale;
        this.invalidateWorldTransform();
        this.broadcastLocalTransformUpdate(props);
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelScale(this, this.transform.scale, meta));
    }
    /// Scene getters and setters ///
    /**
     * Returns the room model that this model resides in
     */
    getRoom() { return this.room; }
    /**
     * Returns the room model that this model resides in or throw an error if no room was specified
     */
    getRoomOrThrow() {
        if (this.room === undefined)
            throw new Error(`Model ${this.getName()} has no associated room`);
        return this.room;
    }
    /**
     * Returns how many rooms deep this model is located. By convention, the root model has depth 0.
     */
    getRoomDepth() {
        if (this.isRoot())
            return 0;
        if (this.room === undefined)
            throw new Error(`Model ${this.getName()} has no associated room`);
        let depth = 0;
        let cur = this.room;
        while (cur !== undefined && !cur.isRoot()) {
            depth++;
            cur = cur.getRoomOrThrow();
        }
        return depth;
    }
    hasParent() { return this.getParent() !== undefined; }
    getParent() { return this.parent; }
    /**
     * Returns the parent model that this model resides in or throw an error if no parent was specified
     */
    getParentOrThrow() {
        if (this.parent === undefined)
            throw new Error(`Model ${this.getName()} has no associated parent`);
        return this.parent;
    }
    /**
     * Find and return a child model with the specified uid or name
     */
    getChild(uidOrName) {
        if (typeof uidOrName === 'string') {
            for (const child of this.children) {
                if (child.getName() === uidOrName)
                    return child;
            }
        }
        else {
            for (const child of this.children) {
                if (child.getUID() === uidOrName)
                    return child;
            }
        }
        return undefined;
    }
    /**
     * Checks if this model has a child model with the specified uid or name
     */
    hasChild(uidOrName) {
        if (typeof uidOrName === 'string')
            return this.children.some(child => child.getName() === uidOrName);
        else
            return this.children.some(child => child.getUID() === uidOrName);
    }
    getChildren() { return this.children; }
    /**
     * Traverses this model and all of its descendants. If includeThis is false then the callback will start with the
     * children of this model and leave out this model
     */
    traverse(callback, includeThis = true) {
        const queuedModels = [this];
        while (queuedModels.length > 0) {
            const model = queuedModels.shift();
            if (model === undefined)
                continue;
            if (includeThis || model !== this)
                callback(model);
            queuedModels.push(...model.getChildren());
        }
    }
    /**
     * Traverses this model and all of its ancestors. If includeThis is false then the callback will start with the parent
     * of this model and leave out this model
     */
    traverseUp(callback, includeThis = true) {
        const queuedModels = [this];
        while (queuedModels.length > 0) {
            const model = queuedModels.shift();
            if (model === undefined)
                continue;
            if (includeThis || model !== this)
                callback(model);
            if (model.hasParent())
                queuedModels.push(model.getParentOrThrow());
        }
    }
    // ToDo: Maybe don't allow user to set the room explicitly but rather use it internally to avoid the case when
    //  this model and the parent of this model live in different rooms
    /**
     * Updates the room this model resides in. The model will be removed from the previous room it resided in
     *
     * @see {@link BroadcastProps}
     */
    setRoom(room, props) {
        if (this.room === room)
            return;
        // Remove model from old room
        if (this.room !== undefined && this.room !== room) {
            const oldRoom = this.room;
            oldRoom.removeChildInRoom(this);
        }
        // Add model to new room
        this.room = room;
        this.room.addChildInRoom(this);
        this.invalidateWorldTransform();
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelRoom(this, room, meta));
        if (shouldBroadcast(props)) {
            const msg = new ModelRoomChangedMessage(this.getUID(), room.getUID());
            this.queueMessage(msg, props);
        }
    }
    /**
     * Updates the parent of this model. If the model had another parent before, it will be automatically detached from
     * the old parent.
     *
     * Setting a new parent will also trigger this model and its descendants to broadcast their new transforms. If you
     * don't want this to happen consider setting `broadcastTransform` to false (default: true)
     *
     * @see {@link BroadcastProps}
     */
    setParent(parent, props) {
        var _a;
        if (this.parent === parent)
            return;
        if (parent === this)
            throw new Error(`Model ${this.getName()} cannot set itself as its own parent`);
        // A child can always only belong to one parent
        if (this.parent !== undefined) {
            // It should be enough if we inform the others that the parent was changed and the rest should be handled by
            // the respective model on the other server / peer / worker-thread side
            // We also don't want to disconnect the parent from this model because we will change the parent afterward anyway
            this.parent.removeChild(this, Object.assign(Object.assign(Object.assign({}, props), { updateParent: "ignore" }), NO_MESSAGING));
        }
        this.parent = parent;
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelParent(this, parent, meta));
        // It should be enough if we inform the others that the parent was changed and the rest should be handled implicitly
        // by the respective model on the other server / peer / worker-thread side
        parent.addChild(this, Object.assign(Object.assign({}, props), NO_MESSAGING));
        // Update room if the room has changed
        if ((parent.isRoom() && parent !== this.room) || (!parent.isRoom() && parent.getRoom() !== this.room)) {
            const newRoom = (parent.isRoom()) ? parent : parent.getRoom();
            if (newRoom !== undefined)
                this.setRoom(newRoom, Object.assign(Object.assign({}, props), NO_MESSAGING));
        }
        this.invalidateWorldTransform();
        if (shouldBroadcast(props)) {
            const msg = new ModelParentChangedMessage(this.getUID(), parent.getUID());
            this.queueMessage(msg, Object.assign({}, props));
        }
        // Update itself and its child transforms
        const broadcastTransform = (_a = props === null || props === void 0 ? void 0 : props.broadcastTransform) !== null && _a !== void 0 ? _a : true;
        if (broadcastTransform)
            this.broadcastLocalTransformUpdate(props);
    }
    /**
     * Adds a new child model. If the child had another parent before, it will be detached from the old parent
     *
     * @see {@link BroadcastProps}
     */
    addChild(child, props) {
        const duplicateChildIndex = this.children.findIndex(existingChild => {
            return existingChild === child || existingChild.getUID() === child.getUID();
        });
        if (duplicateChildIndex >= 0) {
            const existingChild = this.children[duplicateChildIndex];
            if (existingChild === child)
                return;
            this.children.splice(duplicateChildIndex, 1);
        }
        if (child === this)
            throw new Error(`Model ${this.getName()} cannot set itself as its own child`);
        this.children.push(child);
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelChildAdded(this, child, meta));
        // We don't need to inform others that the child's parent changed, it should be enough to inform the other, that a
        // child was added to this model
        child.setParent(this, Object.assign(Object.assign({}, props), NO_MESSAGING));
        if (shouldBroadcast(props)) {
            const msg = new ModelChildChangedMessage(this.getUID(), child.getUID(), ModelChildChangedType.ADDED);
            this.queueMessage(msg, props);
        }
    }
    /**
     * Adds multiple new child models. If the children had another parent before, they will be detached from their old
     * parents
     *
     * @see {@link BroadcastProps}
     */
    addChildren(children, props) {
        for (const child of children) {
            this.addChild(child, props);
        }
    }
    /**
     * Removes a child from this model. With `updateParent` you can specify what happens to the removed child. Keep in
     * mind, that removing a child does *NOT* destroy it
     *
     * @see {@link BroadcastProps}
     */
    removeChild(child, props) {
        var _a;
        const removedChildren = this.children.filter(existingChild => {
            return existingChild === child || existingChild.getUID() === child.getUID();
        });
        if (removedChildren.length === 0)
            return;
        this.children.splice(0, this.children.length, ...this.children.filter(existingChild => {
            return existingChild !== child && existingChild.getUID() !== child.getUID();
        }));
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelChildRemoved(this, child, meta));
        const updateParent = (_a = props === null || props === void 0 ? void 0 : props.updateParent) !== null && _a !== void 0 ? _a : "attachToNextHigherRoom";
        if (updateParent !== "ignore")
            this.detach(child, updateParent, Object.assign(Object.assign({}, props), NO_MESSAGING));
        if (shouldBroadcast(props)) {
            const msg = new ModelChildChangedMessage(this.getUID(), child.getUID(), ModelChildChangedType.REMOVED);
            this.queueMessage(msg, props);
        }
    }
    /// Hierarchy methods ///
    /**
     * Returns true if the other model is an ancestor of this model. If the other model is the same as this model then
     * false is returned.
     *
     * If you want the function to return true even if the models are identical then consider using `isAncestorOfOrEquals`
     */
    isAncestorOf(other) {
        if (this.equals(other))
            return false;
        return this.isAncestorOfRecursive(other);
    }
    /**
     * Returns true if the other model is a descendant of this model. If the other model is the same as this model then
     * false is returned.
     *
     * If you want the function to return true even if the models are identical then consider using `isDescendantOfOrEquals`
     */
    isDescendantOf(other) {
        if (this.equals(other))
            return false;
        return other.isAncestorOfRecursive(this);
    }
    /**
     * Returns true if the other model is an ancestor of this model or if both models are identical
     */
    isAncestorOfOrEquals(other) {
        return this.equals(other) || this.isAncestorOf(other);
    }
    /**
     * Returns true if the other model is a descendant of this model or if both models are identical
     */
    isDescendantOfOrEquals(other) {
        return this.equals(other) || this.isDescendantOf(other);
    }
    /// Facet methods ///
    hasFacet(facetType) {
        if (typeof facetType === "function")
            return this.facets.has(facetType.typestatic);
        return this.getFacetByPattern(facetType.pattern) !== undefined;
    }
    /**
     * Returns true if this model has all the provided facets
     */
    hasFacets(...facetTypes) {
        return facetTypes.every(facetType => this.hasFacet(facetType));
    }
    /**
     * Returns the request facet or undefined if this model doesn't have the requested facet
     */
    getFacet(facetType) {
        if (typeof facetType === "function")
            return this.facets.get(facetType.typestatic);
        return this.getFacetByPattern(facetType.pattern);
    }
    /**
     * Returns all facets
     */
    getFacets() {
        return Array.from(this.facets.values());
    }
    /**
     * Adds a new facet or overwrites a previous facet of the same facet type
     *
     * @see {@link BroadcastProps}
     */
    setFacet(facet, props) {
        var _a;
        const createdFacet = !this.facets.has(facet.type);
        this.facets.set(facet.type, facet);
        const handleFacet = (_a = props === null || props === void 0 ? void 0 : props.handleFacet) !== null && _a !== void 0 ? _a : true;
        if (handleFacet)
            this.handleFacet(facet, props);
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelFacetChanged(this, facet, createdFacet, meta));
        if (shouldBroadcast(props))
            this.queueMessage(new UpdateFacetOnModelMessage(this.uid, facet), props);
    }
    /**
     * Adds or updates the existing facets with the provided facets
     *
     * @see {@link BroadcastProps}
     */
    setFacets(facets, props) {
        for (const facet of facets) {
            this.setFacet(facet, props);
        }
    }
    /**
     * Removes the facet if the model had a facet of the specified type
     *
     * @see {@link BroadcastProps}
     */
    removeFacet(facetType, props) {
        facetType = this.getFacetType(facetType);
        const facet = this.getFacet(facetType);
        this.facets.delete(facetType.typestatic);
        if (facet === undefined)
            return;
        // If we remove a LocalTransformFacet we want to reset the transform of this model
        if (facet instanceof LocalTransformFacet) {
            const transformProps = Object.assign(Object.assign(Object.assign({}, props), LOCAL_MESSAGING), { updateFacet: false });
            this.setTransformComponents([0, 0, 0], [0, 0, 0, 1], [1, 1, 1], transformProps);
        }
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelFacetRemoved(this, facet, meta));
        if (shouldBroadcast(props))
            this.queueMessage(new RemoveFacetFromModelMessage(this.uid, facetType), props);
    }
    /**
     * Removes all facets the model had
     *
     * @see {@link BroadcastProps}
     */
    removeAllFacets(props) {
        Array
            .from(this.facets.values())
            .forEach((facetType) => { this.removeFacet(facetType, props); });
    }
    /// Event handlers ///
    /**
     * Registers a handler that is called each time the name of this model has changed.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnNameChangedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelNameChangedHandler((model, name) => {
            handler(model, name);
        });
    }
    /**
     * Registers a handler that is called each time the visibility of this model has changed.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnVisibilityChangedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelVisibilityChangedHandler((model, visible) => {
            handler(model, visible);
        });
    }
    /**
     * Registers a handler that is called each time the local transform of this model has changed.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnTransformChangedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelTransformChangedHandler((model, transform) => {
            handler(model, transform);
        });
    }
    /**
     * Registers a handler that is called each time the local position of this model has changed.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnPositionChangedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelPositionChangedHandler((model, position) => {
            handler(model, position);
        });
    }
    /**
     * Registers a handler that is called each time the local orientation of this model has changed.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnOrientationChangedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelOrientationChangedHandler((model, orientation) => {
            handler(model, orientation);
        });
    }
    /**
     * Registers a handler that is called each time the local scale of this model has changed.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnScaleChangedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelScaleChangedHandler((model, scale) => {
            handler(model, scale);
        });
    }
    /**
     * Registers a handler that is called each time the room this model resides in has changed.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnRoomChangedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelRoomChangedHandler((model, room) => {
            handler(model, room);
        });
    }
    /**
     * Registers a handler that is called each time the parent of model has changed.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnParentChangedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelParentChangedHandler((model, parent) => {
            handler(model, parent);
        });
    }
    /**
     * Registers a handler that is called each time a child model was added to this model.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnChildAddedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelChildAddedHandler((model, child) => {
            handler(model, child);
        });
    }
    /**
     * Registers a handler that is called each time a child model was removed from this model.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnChildRemovedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelChildRemovedHandler((model, child) => {
            handler(model, child);
        });
    }
    /**
     * Registers a handler that is called each time a facet was added or updated to this model.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnFacetAddedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelFacetChangedHandler((model, facet, created) => {
            handler(model, facet, created);
        });
    }
    /**
     * Registers a handler that is called each time a facet was removed from this model.
     *
     * If you want to react on every model use the same function in ModelPool instead.
     *
     * This method returns an UnsubscribeHandler that can be used to remove the subscription:
     * ```js
     * // Subscribe to model events
     * const unsubscribe = model.setOn***()
     * // Later unsubscribe again
     * unsubscribe()
     * ```
     */
    setOnFacetRemovedHandler(handler) {
        return MODEL_POOL_ACCESS_FOR_MODEL.setOnModelFacetRemovedHandler((model, facet) => {
            handler(model, facet);
        });
    }
    /// Messaging methods ///
    /**
     * Adds a message to the message queue. The order in which messages are created or added is preserved
     * across multiple models as well as the model pool. You can achieve the same by calling `modelPool.addMessage(...)`.
     * The message is also handled locally by the ModelModule if it has the respective message handler. If you don't want
     * that the ModelModule handles the message set `props.handleLocally` to false
     */
    addMessage(message, props) {
        this.queueMessage(message, Object.assign({ handleMessageInModelModule: true }, props));
    }
    /**
     * Adds multiple messages to the message queue. The order in which messages are created or added is preserved
     * across multiple models as well as the model pool. You can achieve the same by calling `modelPool.addMessages(...)`.
     * The message is also handled locally by the ModelModule if it has the respective message handler. If you don't want
     * that the ModelModule handles the message set `props.handleLocally` to false
     */
    addMessages(messages, props) {
        messages.forEach(message => {
            this.queueMessage(message, Object.assign({ handleMessageInModelModule: true }, props));
        });
    }
    /**
     * Sends all the messages that were created by this or any other model as well as the model pool. You can also
     * just call `modelPool.flushMessages()` to achieve the same effect.
     */
    flushMessages(props) {
        MODEL_POOL_ACCESS_FOR_MODEL.flushMessages(props);
    }
    /// Methods that should not be called by the user ///
    /**
     * This should never be called by the user. It is used by the ModelPool internally when deleting this model
     */
    _detachForDeletion() {
        var _a, _b;
        (_a = this.parent) === null || _a === void 0 ? void 0 : _a.removeChild(this, Object.assign({ updateParent: "ignore" }, NO_MESSAGING));
        (_b = this.room) === null || _b === void 0 ? void 0 : _b.removeChild(this, Object.assign({ updateParent: "ignore" }, NO_MESSAGING));
        this.parent = undefined;
        if (this.children.length !== 0)
            throw new Error(`Children of model ${this.getName()} have to be removed first before detaching model for deletion`);
        this.children.length = 0;
    }
    /// Appearance helper methods ///
    broadcastLocalTransformUpdate(props) {
        props = applyDefaults(DefaultTransformProps, props !== null && props !== void 0 ? props : {});
        emitOrDefer(props, (meta) => MODEL_POOL_ACCESS_FOR_MODEL.invokeModelTransform(this, this.transform.matrix, meta));
        // We simply set the facet without notifying anyone
        let localTransformFacet;
        if (props.updateFacet === true) {
            const pos = this.transform.position.toArray();
            const ori = this.transform.orientation.toArray();
            const sca = this.transform.scale.toArray();
            // Only update the facet if anything has changed
            const nextLocalTransformFacet = new LocalTransformFacet(pos, ori, sca);
            const currentLocalTransformFacet = this.getFacet(LocalTransformFacet);
            if (currentLocalTransformFacet === undefined || !currentLocalTransformFacet.equals(nextLocalTransformFacet)) {
                localTransformFacet = nextLocalTransformFacet;
                this.setFacet(localTransformFacet, Object.assign(Object.assign(Object.assign({}, props), { handleFacet: false }), NO_MESSAGING));
            }
        }
        // Either send an UpdateFacetOnModelMessage or an UpdateLocalTransformationMessage
        if (shouldBroadcast(props)) {
            if (localTransformFacet !== undefined) {
                this.queueMessage(new UpdateFacetOnModelMessage(this.uid, localTransformFacet));
                if (props.sendBothFacetAndMessage === true)
                    this.queueMessage(new UpdateLocalTransformationMessage(this.uid, convertToNumber16(this.transform.matrix)), props);
            }
            else {
                this.queueMessage(new UpdateLocalTransformationMessage(this.uid, convertToNumber16(this.transform.matrix)), props);
            }
            // Rooms send a special room transformation update message
            if (this.isRoom()) {
                this.queueMessage(new RoomTransformationUpdateMessage(this.getUID(), this.getWorldTransform().toArray()));
            }
        }
        // If true we send messages for the children within this thread
        if (props.handleDescendantTransforms === true) {
            this.children.forEach(child => {
                // For the children we only send message locally since this will be done by each worker individually
                child.broadcastLocalTransformUpdate(Object.assign(Object.assign({}, props), LOCAL_MESSAGING));
            });
        }
    }
    recomputeWorldTransform() {
        const newWorldMatrix = this.transform.matrix;
        if (this.parent !== undefined)
            newWorldMatrix.premultiply(this.parent.getWorldTransform());
        this.worldTransform.matrix = newWorldMatrix;
        this.worldTransformDirty = false;
    }
    /// Facet helper methods ///
    handleFacet(facet, props) {
        var _a;
        // We use local messaging internally since we broadcast a UpdateFacetOnModelMessage to everyone else. Thus, each
        // worker does the same internal handling. If NO_MESSAGING had been specified then we don't broadcast anything
        // locally
        const internalProps = Object.assign(Object.assign({}, props), LOCAL_MESSAGING);
        if (facet instanceof LocalTransformFacet) {
            this.setTransformComponents(facet.position, facet.orientation, facet.scale, Object.assign(Object.assign({}, internalProps), { updateFacet: false }));
        }
        else if (facet instanceof VisibilityFacet) {
            this.setVisible(facet.visible, Object.assign(Object.assign({}, internalProps), { handleDescendants: false, updateFacet: false }));
        }
        else if (facet instanceof AttachToModelFacet) {
            const target = modelPool.getModel((_a = facet.targetUid) !== null && _a !== void 0 ? _a : INVALID_UID);
            if (target !== undefined) {
                this.setParent(target, internalProps);
                if (facet.params.offsetMatrix !== undefined)
                    this.setTransform(facet.params.offsetMatrix, internalProps);
            }
            else {
                console.warn(`[model] Cannot attach model to new parent ${determineModelNameForPrinting(facet.targetUid)}, because the parent doesn't exist`);
            }
        }
    }
    getFacetByPattern(pattern) {
        for (const facet of this.facets.values()) {
            if (facet.pattern === pattern)
                return facet;
        }
        return undefined;
    }
    /// Scene helper methods ///
    detach(model, updateParent, props) {
        var _a, _b;
        switch (updateParent) {
            case "attachToNextHigherRoom":
                {
                    let newParent = (_a = this.room) !== null && _a !== void 0 ? _a : modelPool.getRoot();
                    if (newParent === this.parent) {
                        if (newParent.isRoot())
                            throw new Error(`Trying to detach model ${this.getName()} from the root using the strategy "${updateParent}" use {updateParent: "ignore"} instead`);
                        newParent = (_b = newParent.room) !== null && _b !== void 0 ? _b : modelPool.getRoot();
                    }
                    model.setParent(newParent, props);
                }
                break;
            case "attachToRoot":
                model.setParent(modelPool.getRoot(), props);
                break;
        }
    }
    isAncestorOfRecursive(other) {
        if (this === other)
            return true;
        return this.children.some((child) => child.isAncestorOfRecursive(other));
    }
    /// Facet type helper method ///
    getFacetType(facetOrFacetType) {
        if (typeof facetOrFacetType === "function")
            return facetOrFacetType;
        return facetOrFacetType.constructor;
    }
    /// Messaging helper methods ///
    queueMessage(message, props) {
        MODEL_POOL_ACCESS_FOR_MODEL.queueMessage(message, props);
    }
}
/**
 * A room model is just a model with additional attributes and methods. It stores all models and their descendents that
 * reside in this room. When adding or removing a child they are automatically added to the room.
 */
export class RoomModel extends Model {
    constructor() {
        super(...arguments);
        this.childrenInRoom = new Set();
        this.childRooms = new Set();
    }
    isRoom() {
        return true;
    }
    /// Children methods ///
    addChild(child, props) {
        super.addChild(child, props);
        this.addChildInRoom(child);
    }
    removeChild(child, props) {
        super.removeChild(child, props);
        this.removeChildInRoom(child);
    }
    /**
     * Adds this model and all descendents of this model. If any model is a room it is instead added as a child room and its
     * children are ignored, because they don't belong to `this` room.
     */
    addChildInRoom(model) {
        if (model.isRoom()) {
            this.removeChildRoom(model);
            this.addChildRoom(model);
            this.removeModelReferenceFromRoom(model);
            this.childrenInRoom.add(model);
            return;
        }
        this.removeModelReferenceFromRoom(model);
        this.childrenInRoom.add(model);
        for (const child of model.getChildren()) {
            this.addChildInRoom(child);
        }
    }
    /**
     * Removes this model and all descendents of this model from this room
     */
    removeChildInRoom(model) {
        if (model.isRoom()) {
            this.removeChildRoom(model);
            this.removeModelReferenceFromRoom(model);
            return;
        }
        this.removeModelReferenceFromRoom(model);
        for (const child of model.getChildren()) {
            this.removeChildInRoom(child);
        }
    }
    removeModelReferenceFromRoom(model) {
        for (const existingModel of this.childrenInRoom) {
            if (existingModel === model || existingModel.getUID() === model.getUID()) {
                this.childrenInRoom.delete(existingModel);
            }
        }
    }
    /**
     * Returns all models in this room that are not rooms themselves
     *
     * ToDo: This might be wrong behaviour. Child room models are still part of this room, right?
     */
    getChildrenInRoom() {
        return Array.from(this.childrenInRoom.values());
    }
    /// Child room methods ///
    /**
     * This is already automatically called by `addChild`
     */
    addChildRoom(childRoom) {
        this.childRooms.add(childRoom);
    }
    /**
     * This is already automatically called by `removeChild`
     */
    removeChildRoom(childRoom) {
        this.childRooms.delete(childRoom);
    }
    /**
     * Returns all child rooms of this room
     */
    getChildRooms() {
        return Array.from(this.childRooms.values());
    }
    /**
     * Checks if a direct child room with the specified uid or room model exists
     */
    hasChildRoom(childOrUID) {
        if (childOrUID instanceof RoomModel) {
            return this.childRooms.has(childOrUID);
        }
        for (const childRoom of this.childRooms.values()) {
            if (childRoom.getUID() === childOrUID)
                return true;
        }
        return false;
    }
    /// Traversal methods ///
    /**
     * Traverses this room and all of its descendant rooms. If includeThis is false then the callback will start with the
     * child room of this room and leave out this room
     */
    traverseRooms(callback, includeThis = true) {
        const queuedModels = [this];
        while (queuedModels.length > 0) {
            const model = queuedModels.shift();
            if (model === undefined)
                continue;
            if (includeThis || model !== this)
                callback(model);
            queuedModels.push(...model.getChildRooms());
        }
    }
}
/**
 * The root model can only exist once and is created automatically by the ModelPool. Never try to create a root model
 * by yourself
 */
export class RootModel extends RoomModel {
    constructor(uid) {
        super(uid, ROOT_ID);
    }
    /// General getters and setters ///
    setName(name) {
        console.error(`[model] Cannot change name of the root model to "${name}"!`);
    }
    /// Scene getters and setters ///
    isRoot() { return true; }
    getParent() {
        return undefined;
    }
    setRoom(room) {
        console.error(`[model] Cannot assign a parent room "${room.getName()}" to the root model!`);
    }
    setParent(parent) {
        console.error(`[model] Cannot assign a parent "${parent.getName()}" to the root model!`);
    }
    /// Facet methods ///
    hasFacet(_facetType) { return false; }
    // ToDo: Should the root be allowed to have facets?
    getFacet(_facetType) { return undefined; }
}
